// src/pages/Home.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import OrderButtonDoorDash from '../components/OrderButtonDoorDash';
import PickupButton from '../components/PickupButton';
import OrderOnlineModal from '../components/OrderOnlineModal';
import MenuButton from '../components/MenuButton';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: #ffffff;
  color: #111111;
  @media (max-width: 768px) { flex-direction: column; }
`;

const Left = styled.div`
  flex: 1;
  position: relative;
  min-height: 100vh;
  background-color: #000;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 50px;
  overflow: hidden;
  @media (max-width: 768px) { padding-right: 0; padding-top: 20px; justify-content: center; }
`;

const Slide = styled.div`
  position: absolute; inset: 0;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.src});
  opacity: ${props => (props.visible ? 1 : 0)};
  transition: opacity 1s ease-in-out;
`;

const Logo = styled.img`
  width: 300px;
  position: relative;
  max-width: 100%;
  height: auto;
  @media (max-width: 768px) { width: 150px; }
`;

const Right = styled.div`
  flex: 1;
  background: #ffffff;
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  padding: 32px 50px 48px;
  @media (max-width: 768px) { align-items: center; padding: 24px 16px 40px; }
`;

// Hidden legacy title (kept for compatibility)
const Title = styled.h1`
  display: none;
`;

const BrandTitle = styled.h1`
  margin: 0 0 8px;
  font-size: clamp(2.5rem, 6vw, 4rem);
  line-height: 1.05;
  font-family: 'Lobster', cursive;
  color: #111111;
  text-align: center;
`;

const SubTitle = styled.h2`
  margin: 0 0 18px;
  font-size: 1.1rem;
  color: #ff761a;
  letter-spacing: 0.08em;
  font-weight: 700;
  text-align: center;
`;

const Buttons = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 16px; margin-top: 12px;
`;

const OrderOnlineBtn = styled.button`
  display: inline-block;
  padding: 14px 28px;
  font-size: 1.1rem;
  border-radius: 999px;
  background: #ff761a;
  color: #ffffff;
  font-family: 'Lobster', cursive;
  transition: transform 0.05s ease, opacity 0.2s ease;
  text-align: center;
  cursor: pointer;
  border: none;
  &:hover { opacity: 0.95; }
  &:active { transform: translateY(1px); }
`;

const LocationsHeading = styled.h2`
  margin: 8px 0 8px;
  font-size: 1.2rem;
  color: #111;
  font-weight: 700;
  text-align: center;
`;

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 760px;
  margin: 12px auto 0;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const LocationCard = styled.div`
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
`;

const LocationName = styled.h3`
  margin: 0 0 8px;
  font-family: 'Lobster', cursive;
  font-size: 1.6rem;
  color: #111;
`;

const LocationDetail = styled.div`
  color: #333;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const FreshSection = styled.section`
  width: 100%;
  max-width: 760px;
  margin: 18px auto 8px;
  text-align: center;
`;

const FreshHeading = styled.h3`
  margin: 0 0 6px;
  font-size: 1.2rem;
  color: #111;
  font-weight: 700;
  text-align: center;
`;

const FreshBlurb = styled.p`
  margin: 0;
  font-family: 'Lobster', cursive;
  color: #ff761a;
  font-size: 1.05rem;
  line-height: 1.5;
`;

/* ---------- Modal ---------- */
const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 50;
`;

const Modal = styled.div`
  width: 92%; max-width: 520px;
  background: #ffffff;
  border: 1px solid #e6e6e6; border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
`;

const ModalTitle = styled.h3`
  margin: 0 0 14px; color: #111; font-size: 1.25rem;
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const CloseBar = styled.div`
  display: flex; justify-content: flex-end; margin-top: 14px;
`;

const CloseBtn = styled.button`
  background: transparent; color: #111;
  border: 1px solid #bbb; border-radius: 999px;
  padding: 8px 14px; cursor: pointer;
`;

const Home = () => {
  const navigate = useNavigate();
  const slideImages = ['/left-bg1.jpg', '/left-bg2.jpg', '/left-bg3.jpg'];
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const [open, setOpen] = useState(false);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const cardsRef = useRef([]);
  const buttonsRef = useRef(null);
  const brandRef = useRef(null);
  const subRef = useRef(null);
  const freshHeadingRef = useRef(null);
  const freshBlurbRef = useRef(null);

  const locations = [
    {
      location: '4500 Washington Ave. Ste #350 Houston, Texas 77007',
      hours: 'Mon-Sun 11:30am-9pm',
      phone: '(209) 650-6912',
    },
    { location: '1737 W. 34th St. Ste #100 Houston, Texas 77018', hours: 'Mon-Sun 11:30am-9pm', phone: '(281) 844-5445' },
    { location: '11195 Westheimer Rd. Houston, Texas 77042', hours: 'Mon-Sun 11:30am-9pm', phone: '(281) 844-5445' },
    {
      location: '6700 Santa Rita Rd. Ste I Pleasanton, CA. 94588',
      hours: [
        'Mon-Fri 11:30am-8pm',
        'Sat 11:30am-7pm',
        'Sun 11:30am-3:30pm'
      ],
      phone: '(510) 828-5313',
    },
  ];

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrent(c => (c + 1) % slideImages.length);
        setVisible(true);
      }, 2500);
    }, 7000);
    return () => clearInterval(cycle);
  }, [slideImages.length]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // GSAP entrance animations
  useEffect(() => {
    const staged = [
      brandRef.current,
      subRef.current,
      freshHeadingRef.current,
      freshBlurbRef.current,
      buttonsRef.current,
      ...cardsRef.current,
    ].filter(Boolean);
    if (staged.length) gsap.set(staged, { opacity: 0, y: 12 });
    if (leftRef.current) gsap.fromTo(leftRef.current, { opacity: 0 }, { opacity: 1, duration: 1, ease: 'power2.out' });
    if (rightRef.current) gsap.fromTo(rightRef.current, { opacity: 0, x: 12 }, { opacity: 1, x: 0, duration: 1, delay: 0.3, ease: 'power2.out' });
    // Headings and blurb
    if (brandRef.current) gsap.to(brandRef.current, { opacity: 1, y: 0, duration: 0.5, delay: 0.35, ease: 'power2.out' });
    if (subRef.current) gsap.to(subRef.current, { opacity: 1, y: 0, duration: 0.5, delay: 0.55, ease: 'power2.out' });
    if (freshHeadingRef.current) gsap.to(freshHeadingRef.current, { opacity: 1, y: 0, duration: 1, delay: 0.60, ease: 'power2.out' });
    if (freshBlurbRef.current) gsap.to(freshBlurbRef.current, { opacity: 1, y: 0, duration: 1, delay: 0.66, ease: 'power2.out' });
    // Cards and buttons
    if (cardsRef.current?.length) gsap.to(cardsRef.current, { opacity: 1, y: 0, duration: 1, delay: 0.85, stagger: 0.08, ease: 'power2.out' });
    if (buttonsRef.current) gsap.to(buttonsRef.current, { opacity: 1, y: 0, duration: 1, delay: 0.90, ease: 'power2.out' });
  }, []);

  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return (
    <Container>
      <Left ref={leftRef}>
        <Slide src={slideImages[current]} visible={visible} />
        <Logo src="/LOGO.jpg" alt="Local Poké" />
      </Left>

      <Right ref={rightRef}>
        <BrandTitle ref={brandRef}>Local Poké</BrandTitle>
        <Title>LOCAL POKE</Title>
        <SubTitle ref={subRef}>FEED ME. LOCAL POKÉ</SubTitle>

        <LocationsHeading>Locations</LocationsHeading>
        <LocationsGrid>
          {locations.map((loc, idx) => (
            <LocationCard key={idx} ref={el => (cardsRef.current[idx] = el)}>
              <LocationName>{loc.location}</LocationName>
              <LocationDetail>
                <strong>Hours:</strong>{' '}
                {Array.isArray(loc.hours)
                  ? loc.hours.map((line, i) => <div key={i}>{line}</div>)
                  : loc.hours}
              </LocationDetail>
              <LocationDetail><strong>Phone:</strong> {loc.phone}</LocationDetail>
            </LocationCard>
          ))}
        </LocationsGrid>

        <FreshSection>
          <FreshHeading ref={freshHeadingRef}>Fresh, Clean, and Delicious</FreshHeading>
          <FreshBlurb ref={freshBlurbRef}>
            At Local Poké we bring the classic Hawaiin Dish to th emainland while providing a modern day twist to this timeless classic. Honoring the past by providing daily marinated options as well as a completely cuztomizable experience is our mission. Healthy fast options are becoming a staple for an ever health conscious trending society and poke fits this role like fish in a bowl.
          </FreshBlurb>
        </FreshSection>

        <Buttons ref={buttonsRef}>
          <OrderOnlineBtn onClick={openModal}>Order Online</OrderOnlineBtn>
          <MenuButton />
        </Buttons>
      </Right>

      <OrderOnlineModal open={open} onClose={closeModal} />
    </Container>
  );
};

export default Home;

